import { FC, useEffect, Fragment } from 'react';
import {
    Banner,
    BodyText,
    Button,
    ButtonGroup,
    FilePicker,
    Headline,
    Icon,
    Popover,
    Spinner,
    Stack,
    Thumbnail,
} from '@servicetitan/design-system';
import { observer } from 'mobx-react';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { AvatarFileUploadStore, validExtensions } from '../stores/avatar-file-upload.store';
import {
    editOverlay,
    iconAdd,
    iconEdit,
    thumbnailDefault,
    previewThumbnailEmpty,
} from './avatar-file-upload.module.less';
import classnames from 'classnames';
import { InputFieldState } from '@servicetitan/form';

export interface AvatarFileUploadProps {
    title: string;
    importUrl: string;
    height?: string | number;
    width?: string | number;
    maxResolution?: number;
    fieldState: InputFieldState<string | undefined>;
    className?: string;
    getPhotoUrl?(path: string, size: number): string;
}

export const AvatarFileUpload: FC<AvatarFileUploadProps> = provide({
    singletons: [AvatarFileUploadStore],
})(
    observer(
        ({
            title,
            fieldState,
            height,
            width,
            maxResolution,
            importUrl,
            className,
            getPhotoUrl = path => path,
        }) => {
            const [
                {
                    init,
                    isPopoverOpen,
                    file,
                    isLoading,
                    error,
                    previewImageUrl,
                    togglePopover,
                    onUpload,
                    onFileLoad,
                    onFileReplace,
                    onDelete,
                    isPreviewImageLoading,
                },
            ] = useDependencies(AvatarFileUploadStore);

            useEffect(() => {
                init(fieldState.value);
            }, [init, fieldState]);

            const photoUrl = fieldState?.value;

            const handleUpload = async () => {
                const tempFileName = await onUpload(importUrl, maxResolution);
                if (tempFileName) {
                    fieldState.onChange(tempFileName);
                }
            };

            const handleDelete = () => {
                fieldState.onChange(undefined);
                onDelete();
            };

            return (
                <Fragment>
                    <Popover
                        portal
                        open={isPopoverOpen}
                        onClickOutside={() => togglePopover(isLoading)}
                        trigger={
                            photoUrl ? (
                                <Thumbnail
                                    size={Thumbnail.Sizes.S}
                                    src={getPhotoUrl(photoUrl, 64)}
                                    className={classnames(editOverlay, className)}
                                    onClick={() => togglePopover()}
                                    fill="cover"
                                    style={{ height, width }}
                                    overlay={
                                        <Stack
                                            alignItems="center"
                                            justifyContent="center"
                                            className="w-100 h-100"
                                        >
                                            <Icon name="edit" className="c-blue-400" size={18} />
                                        </Stack>
                                    }
                                />
                            ) : (
                                <Stack
                                    onClick={() => togglePopover()}
                                    alignItems="center"
                                    justifyContent="center"
                                    style={{ width, height }}
                                    className={thumbnailDefault}
                                >
                                    <Icon name="add" size={18} className={iconAdd} />
                                    <Icon name="edit" size={18} className={iconEdit} />
                                </Stack>
                            )
                        }
                        footerAlign="right"
                        footer={
                            !file ? undefined : (
                                <ButtonGroup>
                                    <Button
                                        disabled={isLoading}
                                        onClick={() => togglePopover(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button primary onClick={handleUpload} loading={isLoading}>
                                        Upload
                                    </Button>
                                </ButtonGroup>
                            )
                        }
                    >
                        <Stack direction="column" spacing={2}>
                            <Headline size="small">{title}</Headline>
                            {error && (
                                <Banner status="critical" title="Upload error">
                                    {error}
                                </Banner>
                            )}
                            <FilePicker
                                value={file ? [file] : undefined}
                                accept={validExtensions.join(', ')}
                                onSelected={files => onFileLoad(files)}
                                onReplace={onFileReplace}
                                onDelete={handleDelete}
                            />
                            {previewImageUrl || photoUrl ? (
                                <Thumbnail
                                    fill="cover"
                                    size={Thumbnail.Sizes.L}
                                    src={previewImageUrl ?? getPhotoUrl(photoUrl!, 94)}
                                />
                            ) : (
                                <Stack
                                    alignItems="center"
                                    justifyContent="center"
                                    className={classnames(
                                        'bg-neutral-40 ta-center',
                                        previewThumbnailEmpty
                                    )}
                                    id="image-preview"
                                >
                                    {isPreviewImageLoading ? (
                                        <Spinner size="small" />
                                    ) : (
                                        <BodyText size="xsmall" className="w-75">
                                            No File Selected
                                        </BodyText>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    </Popover>
                </Fragment>
            );
        }
    )
);

AvatarFileUpload.displayName = 'AvatarFileUpload';
